class SliderComponent extends HTMLElement {
    constructor() {
        super();
        this.boundUpdate = this.update.bind(this);
        this.boundButtonClick = this.onButtonClick.bind(this);
        this.boundDotClick = this.onDotClick.bind(this);
        this.rtl_num = document.body.classList.contains('layout_rtl') ? -1 : 1;
    }

    connectedCallback() {
        this.resizeObserver = new ResizeObserver(() => this.initPages());
        this.observerSubtree = new MutationObserver(records => {
            if (records.some(record => record.target === this || this.slider?.contains(record.target) ||
                [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 &&
                    (node.matches('.slider, ul, button') || node.querySelector('.slider, button'))))) this.refresh();
        });
        this.observerSubtree.observe(this, { childList: true, subtree: true });
        this.refresh();
    }

    disconnectedCallback() {
        this.unbind();
        this.resizeObserver?.disconnect();
        this.observerSubtree?.disconnect();
        this.edgeObserver?.disconnect();
    }

    unbind() {
        this.slider?.removeEventListener('scroll', this.boundUpdate);
        this.prevButton?.removeEventListener('click', this.boundButtonClick);
        this.nextButton?.removeEventListener('click', this.boundButtonClick);
        this.dotButton?.forEach(button => button.removeEventListener('click', this.boundDotClick));
    }

    refresh() {
        this.unbind();
        this.resizeObserver.disconnect();
        this.slider = this.querySelector(this instanceof ProductSliderComponent || this instanceof BannerSliderComponent ? '.slider' : 'ul');
        this.sliderItems = this.slider ? this.slider.querySelectorAll(this instanceof ProductSliderComponent ? '.slider__slide, div.product' : this instanceof BannerSliderComponent ? '.slider__slide' : 'li') : [];
        this.pageCount = this.querySelector('.slider-counter--current');
        this.pageTotal = this.querySelector('.slider-counter--total');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
        this.dotButton = this.querySelectorAll('button[name="dots"]');
        this.sliderActionsAndPageCount = this.querySelector('.slider-buttons');
        if (!this.slider) return;
        this.resizeObserver.observe(this.slider);
        this.slider.addEventListener('scroll', this.boundUpdate);
        this.prevButton?.addEventListener('click', this.boundButtonClick);
        this.nextButton?.addEventListener('click', this.boundButtonClick);
        this.dotButton.forEach(button => button.addEventListener('click', this.boundDotClick));
        this.initPages();
        this.initButtonDisable?.();
    }

    initPages() {
        const width = this.sliderItems[0]?.clientWidth;
        if (!width || !this.slider) return;
        this.slidesPerPage = Math.max(1, Math.floor(this.slider.clientWidth / width));
        this.totalPages = Math.max(1, this.sliderItems.length - this.slidesPerPage + 1);
        this.update();
    }

    update() {
        const width = this.sliderItems[0]?.clientWidth;
        if (!width || !this.slider) return;
        this.currentPage = Math.max(0, Math.round(this.slider.scrollLeft / width * this.rtl_num));
        this.querySelector('.dots-item.active')?.classList.remove('active');
        this.querySelectorAll('.dots-item')[this.currentPage]?.classList.add('active');
        if (this.pageCount) this.pageCount.innerText = Math.min(this.sliderItems.length, this.slidesPerPage + this.currentPage);
    }

    onButtonClick(event) {
        event.preventDefault();
        const width = this.sliderItems[0]?.clientWidth;
        if (!width || !this.slider) return;
        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        this.slider.scrollTo({ left: this.slider.scrollLeft + width * this.rtl_num * direction });
    }

    onDotClick(event) {
        event.preventDefault();
        const index = Number(event.currentTarget.dataset.index);
        const width = this.sliderItems[0]?.clientWidth;
        if (!width || !this.slider || !Number.isInteger(index) || index < 0 || index >= this.sliderItems.length) return;
        this.querySelector('.dots-item.active')?.classList.remove('active');
        event.currentTarget.closest('.dots-item')?.classList.add('active');
        this.slider.scrollTo({ left: index * width * this.rtl_num });
    }
}

class ProductSliderComponent extends SliderComponent {}

class BannerSliderComponent extends SliderComponent {
    initButtonDisable() {
        this.edgeObserver?.disconnect();
        this.sliderItems.forEach(item => item.classList.remove('first-item', 'last-item'));
        if (!this.sliderItems.length) return;
        this.edgeObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.target.classList.contains('first-item')) this.prevButton?.classList.toggle('disabled', entry.isIntersecting);
                if (entry.target.classList.contains('last-item')) this.nextButton?.classList.toggle('disabled', entry.isIntersecting);
            });
        }, { threshold: 0.8, root: this.slider });
        const first = this.sliderItems[0], last = this.sliderItems[this.sliderItems.length - 1];
        first.classList.add('first-item');
        last.classList.add('last-item');
        this.edgeObserver.observe(first);
        this.edgeObserver.observe(last);
    }
}

class TiktokSliderComponent extends BannerSliderComponent {
    initPages() {
        const width = this.sliderItems[0]?.clientWidth;
        if (!width || !this.slider) return;
        const distance = this.slider.clientWidth / width;
        this.slidesPerPage = Math.max(1, distance % 1 > 0.5 ? Math.ceil(distance) : Math.floor(distance));
        this.totalPages = Math.max(1, Math.ceil(this.sliderItems.length - this.slidesPerPage) + 1);
        if (this.pageTotal && this.dataset.tiktokSlider === 'true') this.pageTotal.innerText = this.totalPages;
        if (this.sliderActionsAndPageCount) this.sliderActionsAndPageCount.style.display = this.totalPages <= 1 || window.innerWidth >= 1025 ? 'none' : 'flex';
        this.update();
    }
}

if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);
if (!customElements.get('banner-slider-component')) customElements.define('banner-slider-component', BannerSliderComponent);
if (!customElements.get('product-slider-component')) customElements.define('product-slider-component', ProductSliderComponent);
if (!customElements.get('tiktok-slider-component')) customElements.define('tiktok-slider-component', TiktokSliderComponent);
